import {
    ChangeDetectionStrategy,
    Component,
    input,
    output,
    ViewEncapsulation,
} from '@angular/core';

// ─── Types ───────────────────────────────────────────────────────────────────

/**
 * Pergunta de acompanhamento (Follow Up) sugerida após uma resposta.
 *
 * - `title`: texto exibido no botão.
 * - `question`: texto efetivamente enviado ao selecionar a sugestão.
 */
export type FollowUp = {
    title: string;
    question: string;
};

// ─── Events ──────────────────────────────────────────────────────────────────

/** Evento emitido ao selecionar um Follow Up. */
export type FollowUpSelectedEvent = {
    followUp: FollowUp;
};

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * Lista de perguntas de acompanhamento (Follow Ups) exibida abaixo de uma
 * mensagem do assistente. Renderiza um botão de largura total por sugestão
 * (mostrando o `title`); ao clicar, emite {@link followUpSelected}.
 *
 * Componente apenas de apresentação: não envia mensagens nem traduz textos — o
 * container decide o que fazer com a seleção e passa o `heading` já traduzido.
 */
@Component({
    selector: 'ai-follow-ups',
    standalone: true,
    templateUrl: './follow-ups.component.html',
    styleUrls: ['./follow-ups.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FollowUpsComponent {
    /** Lista de Follow Ups a serem exibidos. */
    readonly followUps = input<FollowUp[]>([]);

    /** Texto opcional exibido acima da lista (já traduzido pelo container). */
    readonly heading = input<string>('');

    /** Emitido ao selecionar (clicar) um Follow Up. */
    readonly followUpSelected = output<FollowUpSelectedEvent>();

    /**
     * Trata o clique em um Follow Up, repassando a seleção ao container.
     *
     * @param followUp Follow Up selecionado.
     */
    protected onSelect(followUp: FollowUp): void {
        this.followUpSelected.emit({ followUp });
    }
}
